import { runInAction } from 'mobx'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { matchPath, useNavigate, useParams } from 'react-router-dom'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import RequestStatusEnum from 'todistukset-ui/src/vtj/RequestStatus.enum'
import {
  validateContactInfoFields,
  validateOrdererInfoFields,
  validateSignerSpouseInfoFields,
} from 'todistukset-common/src/vtj/validation'
import AetContactInfoStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetContactInfoStep.react'
import AetInstructionsStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetInstructionsStep.react'
import AetOrderCompleteStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetOrderCompleteStep.react'
import AetSignerInfoStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSignerInfoStep.react'
import AetLastnameConfirmationStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSignerLastnameConfirmationStep.react'
import AetSignerTodistuspyyntoRejectedStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSignerTodistuspyyntoRejectedStep'
import AetSpouseInfoStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSpouseInfoStep.react'
import AetSummaryStep from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/AetSummaryStep.react'
import {
  OnClickParams,
  Step,
  StepButtons,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useSubmitAetSignerForm } from 'todistukset-ui/src/vtj/stores/actions/aet-signer-form-actions'
import { refreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import { clearVisitedSteps } from 'todistukset-ui/src/vtj/stores/actions/steps-actions'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import PageRoutes from 'todistukset-ui/src/vtj/todistukset-page-routes.enum'
import StepRoutes from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'

// eslint-disable-next-line @typescript-eslint/no-empty-function
function doNothing(): void {}

const refreshOnClick = ({ onSuccess }: OnClickParams): void => {
  refreshSession()
  onSuccess()
}

const getRefreshOnClick = () => refreshOnClick

/**
 *
 * @param pathname url path. Required when getPath function of steps is needed, but left optional to be able to get length of steps
 */
export function getAetSignerSteps({
  pathname,
  onlyDeclineIsAllowed,
}: {
  pathname?: string
  onlyDeclineIsAllowed?: boolean
}): Step[] {
  const steps = [
    {
      name: StepRoutes.AET_SIGNER_INSTRUCTIONS,
      component: AetInstructionsStep,
      validations: [],
      onPrevClick: doNothing,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.NEXT,
    },
    {
      name: StepRoutes.AET_SIGNER_ORDERER_INFO,
      component: AetSignerInfoStep,
      validations: [validateOrdererInfoFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: onlyDeclineIsAllowed ? () => null : getRefreshOnClick,
      buttons: onlyDeclineIsAllowed
        ? StepButtons.PREV_AND_DECLINE
        : StepButtons.PREV_AND_NEXT,
    },
    {
      name: StepRoutes.AET_SIGNER_SPOUSE_INFO,
      component: AetSpouseInfoStep,
      validations: [validateSignerSpouseInfoFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
      isHidden: onlyDeclineIsAllowed,
    },
    {
      name: StepRoutes.AET_SIGNER_LASTNAME_CHOICE,
      component: AetLastnameConfirmationStep,
      validations: [],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
      isHidden: onlyDeclineIsAllowed,
    },
    {
      name: StepRoutes.AET_SIGNER_CONTACT_INFO,
      component: AetContactInfoStep,
      validations: [validateContactInfoFields],
      onPrevClick: refreshOnClick,
      useOnNextClick: getRefreshOnClick,
      buttons: StepButtons.PREV_AND_NEXT,
      isHidden: onlyDeclineIsAllowed,
    },
    {
      name: StepRoutes.AET_SIGNER_SUMMARY,
      component: AetSummaryStep,
      validations: [],
      onPrevClick: refreshOnClick,
      useOnNextClick: useSubmitAetSignerForm,
      buttons: StepButtons.PREV_AND_CONFIRM,
      isHidden: onlyDeclineIsAllowed,
    },
    {
      name: StepRoutes.AET_SIGNER_ORDER_COMPLETE,
      component: AetOrderCompleteStep,
      validations: [],
      onPrevClick: doNothing,
      useOnNextClick: () => doNothing,
      buttons: StepButtons.NONE,
      isHidden: onlyDeclineIsAllowed,
    },
    {
      name: StepRoutes.AET_SIGNER_TODISTUSPYYNTO_REJECTED,
      component: AetSignerTodistuspyyntoRejectedStep,
      validations: [],
      onPrevClick: doNothing,
      useOnNextClick: () => doNothing,
      buttons: StepButtons.NONE,
      isHidden: !onlyDeclineIsAllowed,
    },
  ]
  return steps.map((step, index) => {
    return {
      ...step,
      index,
      getPath: (lang: LanguageCode) => {
        const aetTodistuspyyntoId = getAetTodistuspyyntoIdFromPathname(
          pathname ?? ''
        )
        if (!aetTodistuspyyntoId) {
          throw new Error('Expected pathname to contain aetTodistuspyyntoId')
        }

        return `/${lang}/${PageRoutes.AETTODISTUS_SIGNER}/${aetTodistuspyyntoId}/${step.name}`
      },
    }
  })
}

export const useAetTodistuspyyntoId = (): number => {
  const { aetTodistuspyyntoId } = useParams()
  if (isNaN(Number(aetTodistuspyyntoId))) {
    // aetTodistuspyyntoId is tested in OrderWizardPage already before accessing the AET signer flow where this used
    throw new Error('Type check failed. aetTodistuspyyntoId must be a number.')
  }
  return Number(aetTodistuspyyntoId)
}

const resetSignerFormFlow = (store: TodistuksetUi.Store) => {
  runInAction(() => {
    store.order.requestStatus = RequestStatusEnum.INPUT
  })
  clearVisitedSteps(OrderFlowName.AETTODISTUS_SIGNER)
}

export const useNavigateToAetSignerForm = (): ((
  targetAetTodistuspyyntoId: number,
  lang: LanguageCode
) => void) => {
  const navigate = useNavigate()
  const store = useStore()
  return (targetAetTodistuspyyntoId: number, lang: LanguageCode) => {
    resetSignerFormFlow(store)
    navigate(
      `/${lang}/${PageRoutes.AETTODISTUS_SIGNER}/${targetAetTodistuspyyntoId}/${StepRoutes.AET_SIGNER_INSTRUCTIONS}`
    )
  }
}

export const getAetTodistuspyyntoIdFromPathname = (
  pathname: string
): number | null => {
  const pathMatch = matchPath(
    `/:lang?/${PageRoutes.AETTODISTUS_SIGNER}/:aetTodistuspyyntoId/:step?/:tab?`,
    pathname
  )

  const aetTodistuspyyntoId = Number(pathMatch?.params.aetTodistuspyyntoId)

  if (!isNaN(aetTodistuspyyntoId)) {
    return aetTodistuspyyntoId
  }

  return null
}
